import { computed } from 'vue'
import { session } from '@/composables/useSession'
import { hasAccountRole, hasWorkspaceRole, hasHighestWorkspaceRole } from '@/utils/roleUtils'
import { useDashboardsList } from '@/composables/useDashboardsList'
import i18n from '@/i18n'

export interface DashboardNavItem {
  key: string
  label: string
  routeName: string
  routeParams?: Record<string, any>
  routeQuery?: Record<string, any>
  testId?: string
  analyticsEvent?: string
  isDisabled?: boolean // For fallback items that aren't clickable
}

export function useDashboardNavigation() {
  const {
    lookerAutoQaDashboard,
    lookerReviewsDashboard,
    lookerReviewsAgentDashboard,
    lookerBotQaDashboard,
    lookerAssignmentsDashboard,
    lookerSurveysDashboard,
    lookerCategoriesDashboard,
    lookerCategoriesAgentDashboard,
    lookerCalibrationDashboard,
    lookerCoachingDashboard,
    lookerCalibrationAgentDashboard,
    lookerCalibrationSubmitDashboard,
    lookerDisputesDashboard,
    lookerDisputesAgentDashboard,
  } = useDashboardsList()

  const isAgentInActiveWorkspace = computed(() => {
    return !hasAccountRole('ADMIN', 'MANAGER') && hasWorkspaceRole('AGENT')
  })

  // Reactive titles function
  const getTitle = (key: string) => {
    const titleMap: Record<string, string> = {
      'dashboard.reviews': i18n.t('routes.dashboard.label.reviews'),
      'dashboard.auto-qa': i18n.t('routes.dashboard.label.auto_qa'),
      'dashboard.bot-qa': i18n.t('routes.dashboard.label.bot_qa'),
      'dashboard.assignments': i18n.t('routes.dashboard.label.assignments'),
      'dashboard.surveys': i18n.t('routes.dashboard.label.surveys'),
      'dashboard.disputes': i18n.t('routes.dashboard.label.disputes'),
      'dashboard.calibration': i18n.t('routes.dashboard.label.calibration'),
      'dashboard.category': i18n.t('routes.dashboard.label.categories'),
      'dashboard.coaching': i18n.t('routes.dashboard.label.coaching'),
    }
    return titleMap[key] || key
  }

  const navigationItems = computed<DashboardNavItem[]>(() => {
    const items: DashboardNavItem[] = []

    // Reviews Dashboard
    if (lookerReviewsDashboard.value || lookerReviewsAgentDashboard.value) {
      if (hasWorkspaceRole('AGENT') && lookerReviewsAgentDashboard.value) {
        items.push({
          key: 'dashboard.reviews.agent',
          label: getTitle('dashboard.reviews'),
          routeName: 'dashboard.looker',
          routeParams: { id: lookerReviewsAgentDashboard.value?.id },
          analyticsEvent: 'Looker Agent Reviews dashboard opened',
        })
      } else if (lookerReviewsDashboard.value) {
        items.push({
          key: 'dashboard.reviews.manager',
          label: getTitle('dashboard.reviews'),
          routeName: 'dashboard.looker',
          routeParams: { id: lookerReviewsDashboard.value?.id },
          analyticsEvent: 'Looker Reviews dashboard opened',
        })
      }
    } else {
      // Fallback disabled item
      items.push({
        key: 'dashboard.reviews.disabled',
        label: getTitle('dashboard.reviews'),
        routeName: '',
        isDisabled: true,
      })
    }

    // Auto QA Dashboard
    if (session.features.autoQa && !isAgentInActiveWorkspace.value) {
      if (lookerAutoQaDashboard.value) {
        items.push({
          key: 'dashboard.auto-qa',
          label: getTitle('dashboard.auto-qa'),
          routeName: 'dashboard.looker',
          routeParams: { id: lookerAutoQaDashboard.value?.id },
          analyticsEvent: 'AutoQA dashboard opened',
        })
      } else {
        // Fallback disabled item
        items.push({
          key: 'dashboard.auto-qa.disabled',
          label: getTitle('dashboard.auto-qa'),
          routeName: '',
          isDisabled: true,
        })
      }
    }

    // Bot QA Dashboard
    if (session.features.botQa && !isAgentInActiveWorkspace.value) {
      if (lookerBotQaDashboard.value) {
        items.push({
          key: 'dashboard.bot-qa',
          label: getTitle('dashboard.bot-qa'),
          routeName: 'dashboard.looker',
          routeParams: { id: lookerBotQaDashboard.value?.id },
          analyticsEvent: 'BotQA dashboard opened',
        })
      } else {
        // Fallback disabled item
        items.push({
          key: 'dashboard.bot-qa.disabled',
          label: getTitle('dashboard.bot-qa'),
          routeName: '',
          isDisabled: true,
        })
      }
    }

    // Assignments Dashboard
    if (
      (hasAccountRole('ADMIN', 'MANAGER') || hasHighestWorkspaceRole('MANAGER', 'LEAD')) &&
      lookerAssignmentsDashboard.value
    ) {
      items.push({
        key: 'dashboard.assignments',
        label: getTitle('dashboard.assignments'),
        routeName: 'dashboard.looker',
        routeParams: { id: lookerAssignmentsDashboard.value?.id },
        analyticsEvent: 'Assignments looker dashboard opened',
      })
    }

    // Coaching Dashboard
    if (
      (hasAccountRole('ADMIN', 'MANAGER') || hasHighestWorkspaceRole('MANAGER', 'LEAD')) &&
      lookerCoachingDashboard.value &&
      session.features.lookerCoachingDashboard
    ) {
      items.push({
        key: 'dashboard.coaching',
        label: getTitle('dashboard.coaching'),
        routeName: 'dashboard.looker',
        routeParams: { id: lookerCoachingDashboard.value?.id },
        analyticsEvent: 'Coaching looker dashboard opened',
      })
    }

    // Surveys Dashboard
    if (lookerSurveysDashboard.value) {
      items.push({
        key: 'dashboard.surveys.looker',
        label: getTitle('dashboard.surveys'),
        routeName: 'dashboard.looker',
        routeParams: { id: lookerSurveysDashboard.value?.id },
        analyticsEvent: 'Looker Surveys dashboard opened',
      })
    }

    // Disputes Dashboard
    if (
      (hasAccountRole('ADMIN', 'MANAGER') || hasWorkspaceRole('MANAGER', 'LEAD', 'REVIEWER')) &&
      lookerDisputesDashboard.value
    ) {
      items.push({
        key: 'dashboard.disputes.manager',
        label: getTitle('dashboard.disputes'),
        routeName: 'dashboard.looker',
        routeParams: { id: lookerDisputesDashboard.value?.id },
        analyticsEvent: 'Looker Disputes dashboard opened',
      })
    } else if ((hasAccountRole('USER') || hasWorkspaceRole('AGENT')) && lookerDisputesAgentDashboard.value) {
      items.push({
        key: 'dashboard.disputes.agent',
        label: getTitle('dashboard.disputes'),
        routeName: 'dashboard.looker',
        routeParams: { id: lookerDisputesAgentDashboard.value?.id },
        analyticsEvent: 'Looker Agent Disputes dashboard opened',
      })
    }

    // Calibration Dashboard
    // NOTE: Agents (workspace-only AGENT role) do not have access to calibration dashboards
    // Previously, agents could see the calibration_agent dashboard, but this was now removed as there was no known reason for this.

    if (hasWorkspaceRole('REVIEWER')) {
      // Reviewers: prefer calibration_submit, fallback to calibration_agent
      const dashboardToShow = lookerCalibrationSubmitDashboard.value || lookerCalibrationAgentDashboard.value
      if (dashboardToShow) {
        items.push({
          key:
            dashboardToShow === lookerCalibrationSubmitDashboard.value
              ? 'dashboard.calibration.submit'
              : 'dashboard.calibration.agent',
          label: getTitle('dashboard.calibration'),
          routeName: 'dashboard.looker',
          routeParams: { id: dashboardToShow?.id },
          analyticsEvent: 'Looker Calibration dashboard opened',
        })
      }
    } else if (hasWorkspaceRole('MANAGER', 'LEAD') || hasAccountRole('ADMIN', 'MANAGER')) {
      // Manager, Lead: prefer calibration_submit, fallback to calibration
      const dashboardToShow = lookerCalibrationSubmitDashboard.value || lookerCalibrationDashboard.value
      if (dashboardToShow) {
        items.push({
          key:
            dashboardToShow === lookerCalibrationSubmitDashboard.value
              ? 'dashboard.calibration.submit'
              : 'dashboard.calibration.manager',
          label: getTitle('dashboard.calibration'),
          routeName: 'dashboard.looker',
          routeParams: { id: dashboardToShow?.id },
          analyticsEvent: 'Looker Calibration dashboard opened',
        })
      }
    }

    // Categories Dashboard
    if (lookerCategoriesDashboard.value || lookerCategoriesAgentDashboard.value) {
      if (hasWorkspaceRole('AGENT') && lookerCategoriesAgentDashboard.value) {
        items.push({
          key: 'dashboard.categories.agent',
          label: getTitle('dashboard.category'),
          routeName: 'dashboard.looker',
          routeParams: { id: lookerCategoriesAgentDashboard.value?.id },
          testId: 'categories-dashboard',
          analyticsEvent: 'Looker Categories dashboard opened',
        })
      } else if (lookerCategoriesDashboard.value) {
        items.push({
          key: 'dashboard.categories.manager',
          label: getTitle('dashboard.category'),
          routeName: 'dashboard.looker',
          routeParams: { id: lookerCategoriesDashboard.value?.id },
          testId: 'categories-dashboard',
          analyticsEvent: 'Looker Categories dashboard opened',
        })
      }
    } else {
      // Fallback disabled item
      items.push({
        key: 'dashboard.categories.disabled',
        label: getTitle('dashboard.category'),
        routeName: '',
        isDisabled: true,
      })
    }

    return items
  })

  return {
    navigationItems,
    isAgentInActiveWorkspace,
  }
}
